// Main component for the search page
// Note that all algolia connected components using any of the connect methods
// imported from "react-instantsearch-dom" must be rendered within the <InstantSearch />
// component

import React, { useState } from "react";
import algoliasearch from "algoliasearch";
import {
  connectSearchBox,
  connectStateResults,
  InstantSearch,
  Stats
} from "react-instantsearch-dom";

import PropTypes from "prop-types";

import logo from "images/reskin/small-logo-a-plus.svg";
import number from "helpers/number";
import AuthorEthnicityMultiSelect from "components/search/AuthorEthnicityMultiSelect";
import AuthorGenderMultiSelect from "components/search/AuthorGenderMultiSelect";
import AuthorNationalityMultiSelect from "components/search/AuthorNationalityMultiSelect";
import CurrentRefinements from "components/search/base/CurrentRefinements";
import FilterPromoContext from "components/search/context/FilterPromoContext";
import GenreMultiSelect from "components/search/GenreMultiSelect";
import GradeLevelRefinementList from "components/search/GradeLevelRefinementList";
import GuideRefinementList from "components/search/GuideRefinementList";
import Hits from "components/search/base/Hits";
import IconButton from "components/shared/IconButton";
import LiteraryMovementRefinementList from "components/search/LiteraryMovementRefinementList";
import LiteraryPeriodRefinementList from "components/search/LiteraryPeriodRefinementList";
import MiniAPlus from "components/shared/dialogs/MiniAPlus";
import TypeRefinementList from "components/search/TypeRefinementList";
import TimePeriodRefinementList from "components/search/TimePeriodRefinementList";

const SearchBox = connectSearchBox(({ currentRefinement, refine }) => {
  function refineSearch(event) {
    refine(event.currentTarget.value);
  }

  return (
    <>
      <i className="lc-icon lc-icon-search lc-icon-sm tw-text-gray tw-absolute tw-top-9 tw-ml-3 md:tw-top-12" />
      <input
        className="input-lg search__searchbar !tw-pl-10"
        onChange={refineSearch}
        placeholder="Search"
        type="search"
        value={currentRefinement}
      />
    </>
  );
});

const propTypes = {
  aPlusPromoUrl: PropTypes.string.isRequired,
  algoliaApiKey: PropTypes.string.isRequired,
  algoliaAppId: PropTypes.string.isRequired,
  isAPlus: PropTypes.bool,
  metadataConstants: PropTypes.shape({
    countries: PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
    )
  }).isRequired,
  query: PropTypes.string,
  showRequestContentAfterDelay: PropTypes.number,
  userSignedIn: PropTypes.bool
};

const defaultProps = {
  isAPlus: false,
  query: "",
  showRequestContentAfterDelay: false,
  userSignedIn: false
};

const SearchResults = connectStateResults(
  ({ isSearchStalled, props, showFilters }) => {
    // Loading
    if (isSearchStalled) {
      return "Searching for results...";
    }

    // No results
    return (
      <>
        <div className="search__mobile-filter-toggle search__results">
          <Stats
            translations={{
              stats(nbHits) {
                return `${number.commaSeparateNumber(nbHits)} Results`;
              }
            }}
          />
          <IconButton
            btnClass="btn-md btn-default hits__show-filters"
            iconClass="lc-icon-filters lc-icon-sm"
            onClick={showFilters}
            text="Filter"
          />
        </div>
        <CurrentRefinements />
        <div className="tw-hidden md:tw-block search__results">
          <Stats
            translations={{
              stats(nbHits) {
                return `${number.commaSeparateNumber(nbHits)} Results`;
              }
            }}
          />
        </div>
        <Hits
          aPlusPromoUrl={props.aPlusPromoUrl}
          isAPlus={props.isAPlus}
          promoInfo={props.promoInfo}
          userSignedIn={props.userSignedIn}
          showRequestContentAfterDelay={props.showRequestContentAfterDelay}
        />
      </>
    );
  }
);

const Search = (props) => {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filterPromoContextState, setFilterPromoContextState] = useState({
    currentPromoAttribute: null,
    isAPlus: props.isAPlus,
    setCurrentPromoAttribute: (newPromoAttr) =>
      setFilterPromoContextState((prevState) => ({
        ...prevState,
        currentPromoAttribute: newPromoAttr
      }))
  });

  const searchClient = algoliasearch(props.algoliaAppId, props.algoliaApiKey);

  function showFilters() {
    setShowMobileFilters(true);
    $("body").append(
      "<div class='search__filter-backdrop modal-backdrop' style='opacity: 0.5'></div>"
    );
    // Bind both click and touchend events so it works on both touch and mouse
    document.addEventListener("click", outsideFilterClickListener);
    document.addEventListener("touchend", outsideFilterClickListener);
  }

  function hideFilters() {
    setShowMobileFilters(false);
    $(".search__filter-backdrop").remove();
    document.removeEventListener("click", outsideFilterClickListener);
    document.removeEventListener("touchend", outsideFilterClickListener);
  }

  const outsideFilterClickListener = (event) => {
    const $target = $(event.target);
    if (
      // Clicking on a select option or on the mini promo close button doesn't
      // find search__filters in its ancestor list, probably because they are closed
      // before this method can run, so we specifically exclude them from the check
      !event.target.classList.contains("lc-select__option") &&
      !event.target.classList.contains("lc-select__option--inner") &&
      !event.target.classList.contains("lc-icon-close") &&
      !event.target.classList.contains("lc-select__multi-value") &&
      !event.target.classList.contains("lc-select__multi-value__label") &&
      !$target.closest($(".search__filters")).length &&
      $(".search__filters").is(":visible")
    ) {
      hideFilters();
    }
  };

  function closeMiniPromo() {
    filterPromoContextState.setCurrentPromoAttribute(null);
  }

  // Renders the mini promo to be shown on top of each set of filters when a
  // non a-plus user interacts with them
  const showMiniAplusPromo = (attr) => {
    const { isAPlus, currentPromoAttribute } = filterPromoContextState;
    if (!currentPromoAttribute) return null;

    const { value } = currentPromoAttribute;
    if (isAPlus || value !== attr) return null;

    return (
      <MiniAPlus
        handleClose={closeMiniPromo}
        href={props.aPlusPromoUrl}
        logoSrc={logo}
        shouldRenderCloseBtn
        userSignedIn={props.userSignedIn}
      >
        Upgrade to access these filters
      </MiniAPlus>
    );
  };

  return (
    <div className="container">
      <div className="row">
        <InstantSearch searchClient={searchClient} indexName="litcharts_things">
          <div
            className={`search__filters ${
              showMobileFilters && "search__filters--visible"
            } col-md-4 col-lg-3`}
          >
            <div className="search-filters__inner-container">
              <FilterPromoContext.Provider value={filterPromoContextState}>
                <h2>Filter your search</h2>
                <div className="search__filter-group">
                  {showMiniAplusPromo("category")}
                  <GuideRefinementList />
                </div>
                <div className="search__filter-group">
                  {showMiniAplusPromo("type")}
                  <TypeRefinementList />
                  <GenreMultiSelect />
                </div>
                <div className="search__filter-group">
                  {showMiniAplusPromo("timePeriod")}
                  <TimePeriodRefinementList />
                </div>
                <div className="search__filter-group">
                  {showMiniAplusPromo("literaryPeriod")}
                  <LiteraryPeriodRefinementList />
                </div>
                <div className="search__filter-group">
                  {showMiniAplusPromo("literaryMovement")}
                  <LiteraryMovementRefinementList />
                </div>
                <div className="search__filter-group search__filter-group-author">
                  <div className="refinement-list__header">Author Context</div>
                  {showMiniAplusPromo("author")}
                  <AuthorNationalityMultiSelect
                    countries={props.metadataConstants.countries}
                  />
                  <AuthorEthnicityMultiSelect />
                  <AuthorGenderMultiSelect />
                </div>
                <div className="search__filter-group">
                  {showMiniAplusPromo("gradeLevel")}
                  <GradeLevelRefinementList />
                </div>
              </FilterPromoContext.Provider>
            </div>
          </div>
          <div className="col-xs-12 col-sm-12 col-md-8 col-lg-offset-1 col-lg-8">
            <SearchBox defaultRefinement={props.query} />
            <SearchResults {...props} showFilters={showFilters} />
          </div>
        </InstantSearch>
      </div>
    </div>
  );
};

Search.propTypes = propTypes;
Search.defaultProps = defaultProps;

export default Search;
